import React, { useEffect, useState } from "react";
import { Dialog } from "@primer/react/drafts";
import {
  Box,
  Text,
  TextInput,
  FormControl,
  Button,
  ButtonGroup,
  ToggleSwitch,
} from "@primer/react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { thangDiemApi } from "../../../api/thangDiemApi";
import { ex_thangdiem } from "../../../models/response/thang-diem/ex_thangdiem";
import { useCommonContext } from "../../../contexts/common";
import { NotifyHelper } from "../../../helpers/toast";
import { useCommonSelectedHook } from "../../../hooks/useCommonSelectedHook";

interface ThangDiemDetailModalProps {
  id: number;
  onSuccess: () => void;
  onCancel: () => void;
}

interface ApiResponse {
  is_success: boolean;
  data?: ex_thangdiem;
  message?: string;
}

const ThangDiemDetailModal: React.FC<ThangDiemDetailModalProps> = ({
  id,
  onSuccess,
  onCancel,
}) => {
  const { translate } = useCommonContext();
  const { nam_hoc, dm_coso_id } = useCommonSelectedHook();
  const [isSaving, setIsSaving] = useState(false);
  const [isTextInput, setIsTextInput] = useState(false);

  const defaultValues: ex_thangdiem = {
    id: id,
    thang_diem: "",
    thang_diem_en: "",
    free_text: false,
    diem_toi_da: 0,
    dm_coso_id: dm_coso_id,
  };

  const { setValue, register, handleSubmit, reset, watch } =
    useForm<ex_thangdiem>({
      defaultValues,
    });

  const formValues = watch();

  const loadDetail = async () => {
    if (id > 0) {
      try {
        const res = (await thangDiemApi.detail(id)) as ApiResponse;
        if (res.is_success && res.data) {
          reset(res.data);
          setIsTextInput(res.data.free_text);
        }
      } catch (error) {
        NotifyHelper.Error(translate("Error.LoadDataFailed"));
      }
    }
  };

  useEffect(() => {
    loadDetail();
  }, [id]);

  const onSubmit = async (data: ex_thangdiem) => {
    setIsSaving(true);
    try {
      const api = id > 0 ? thangDiemApi.update : thangDiemApi.insert;
      const res = (await api(data)) as ApiResponse;

      if (res.is_success) {
        NotifyHelper.Success(
          translate(id > 0 ? "Success.Update" : "Success.Create")
        );
        onSuccess();
      } else {
        NotifyHelper.Error(res.message || translate("Error.Generic"));
      }
    } catch (error) {
      NotifyHelper.Error(translate("Error.Generic"));
    }
    setIsSaving(false);
  };

  const handleToggleChange = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsTextInput(!isTextInput);
    setValue("free_text", !formValues.free_text);
  };

  return (
    <Dialog
      title={translate(id > 0 ? "Base.Label.Update" : "Base.Label.Create")}
      onClose={onCancel}
      width="medium"
    >
      <Box as="form" onSubmit={handleSubmit(onSubmit)} sx={{ p: 0 }}>
        <FormControl sx={{ mb: 3 }}>
          <FormControl.Label>{translate("Thang điểm")}</FormControl.Label>
          <TextInput
            {...register("thang_diem")}
            type={isTextInput ? "text" : "number"}
            block
          />
        </FormControl>

        <FormControl sx={{ mb: 3 }}>
          <FormControl.Label>{translate("Thang điểm (En)")}</FormControl.Label>
          <TextInput
            {...register("thang_diem_en")}
            type={isTextInput ? "text" : "number"}
            block
          />
        </FormControl>

        <Box
  sx={{
    display: "grid",
    gridTemplateColumns: "1fr auto", // Changed to 1fr auto
    gap: 3,
    mb: 3,
    alignItems: "center" // Added to vertically center items
  }}
>
  <FormControl>
    <FormControl.Label>
      {translate("Điểm tối đa")}
    </FormControl.Label>
    <TextInput
      {...register("diem_toi_da")}
      type="number"
      block
    />
  </FormControl>

  <FormControl sx={{ pt: 4 }}> {/* Added padding-top to align with input */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <input
        type="checkbox"
        id="free-text-checkbox"
        checked={formValues.free_text}
        onChange={(e) => {
          setIsTextInput(e.target.checked);
          setValue('free_text', e.target.checked);
        }}
      />
      <FormControl.Label htmlFor="free-text-checkbox" sx={{ mb: 0 }}> {/* Removed bottom margin */}
        {translate("Dạng tự do")}
      </FormControl.Label>
    </Box>
  </FormControl>
</Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <ButtonGroup>
            <Button onClick={onCancel}>{translate("Base.Label.Cancel")}</Button>
            <Button variant="primary" type="submit" disabled={isSaving}>
              {translate(id > 0 ? "Base.Label.Update" : "Base.Label.Create")}
            </Button>
          </ButtonGroup>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ThangDiemDetailModal;
